import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", payment_status: null, applied: false });
  const attemptsRef = useRef(0);

  const sessionId = params.get("order_id") || params.get("orderId") || params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, applied: false });
      return;
    }
    let cancelled = false;
    const MAX = 8;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setState({ status: data.status, payment_status: data.payment_status, applied: !!data.applied });
        if (data.payment_status === "paid") {
          await refresh();
          return;
        }
        if (data.status === "expired") return;
        attemptsRef.current += 1;
        if (attemptsRef.current < MAX) setTimeout(poll, 3000);
      } catch (e) {
        toast.error(formatApiError(e));
        setState({ status: "error", payment_status: null, applied: false });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = state.payment_status === "paid";
  const failed = state.status === "expired" || state.status === "error";

  return (
    <div className="min-h-screen grain-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-[#E5E1D8] rounded-2xl p-8 shadow-sm text-center" data-testid="payment-success-page">
        {paid && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#FFF3D6] text-[#8A6D1F] flex items-center justify-center mx-auto"><Clock size={28} /></div>
            <h2 className="mt-4 text-2xl font-semibold" data-testid="payment-success-msg">{t("payment_received")}</h2>
            <p className="text-sm text-[#5C6053] mt-2">{t("awaiting_admin_approval")}</p>
            <div className="mt-6 rounded-xl bg-[#F3EFE7] p-4 text-xs text-[#5C6053]">
              {t("admin_will_review_soon")}
            </div>
            <button
              data-testid="back-to-app-btn"
              onClick={() => navigate("/app")}
              className="mt-6 brand-btn px-5 py-2.5 rounded-lg text-sm"
            >
              {t("back")}
            </button>
          </>
        )}
        {!paid && !failed && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#F3EFE7] text-[#2A5948] flex items-center justify-center mx-auto animate-pulse"><Loader2 size={28} /></div>
            <h2 className="mt-4 text-2xl font-semibold">{t("verifying_payment")}</h2>
            <p className="text-sm text-[#5C6053] mt-1">{t("please_wait")}</p>
          </>
        )}
        {failed && (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#FBE9E4] text-[#A85540] flex items-center justify-center mx-auto"><XCircle size={28} /></div>
            <h2 className="mt-4 text-2xl font-semibold">{t("payment_failed")}</h2>
            <p className="text-sm text-[#5C6053] mt-1">{t("please_try_again")}</p>
            <button onClick={() => navigate("/pay")} data-testid="retry-pay-btn" className="mt-6 brand-btn px-5 py-2.5 rounded-lg text-sm">{t("try_again")}</button>
          </>
        )}
      </div>
    </div>
  );
}
